<template>
  <div class="area-filter move-only">
    <button class="uk-button uk-button-small" uk-toggle="target: #tochituka-area-filter-modal" v-wave>{{ messages.map_select_area_and_pan_to }}</button>
    <div id="tochituka-area-filter-modal" uk-modal>
      <div class="uk-modal-dialog uk-modal-body uk-margin-auto-vertical">
        <div class="uk-modal-header uk-flex uk-flex-between">
          <p class="uk-modal-title-small">{{ messages.map_select_area_and_pan_to }}</p>
          <button class="uk-modal-close uk-modal-close-relative" type="button" uk-close></button>
        </div>
        <div class="uk-modal-body">
          <select v-model="selectedCity" class="uk-select" aria-label="Select City" :disabled="!initialized">
            <option value="" key="0">市町村を選択</option>
            <option v-for="city in cities" :value="city" :key="city">{{ city }}</option>
          </select>
          <button
            class="uk-button move-to-area"
            :disabled="!selectedCity"
            @click="onClickedMove"
            v-wave
          ><span>{{ messages.map_pan_to_selected_area }}</span></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * tochituka専用の「地域を選択して移動」
 * 都道府県セレクトを持たず、市町村（Point.extra_fields.市町村）のみで集約した
 * PointService.aggregates.cities.groups を市町村名だけで直接参照して地図移動を行う
 *
 * 石川県固定などの都道府県による絞り込みは行わない（ポイントが都道府県フィールドを持たない前提のため不要）
 */
import { computed, onMounted, ref, watch } from 'vue'
import uikit from 'uikit'
import store from '@/store'
import MapService from '@/services/MapService.ts'
import PointService from '@/services/PointService.ts'

/**
 * 集約したPointの1グループ
 */
interface AggregatedAreaGroup {
  center: {
    latitude: number
    longitude: number
  }
  bounds: {
    latitude: number
    longitude: number
  }[]
}

const messages = storelocator.messages

/**
 * デフォルトのポイントが1つの場合のズームレベル
 */
const DEFAULT_POINT_ZOOM = 16

/**
 * デフォルトのポイントが複数の場合の最大ズームレベル
 */
const DEFAULT_POINTS_MAX_ZOOM = 14

/**
 * ズーム設定は共通のarea_filter設定があれば流用する（enabledはfalseのまま未使用）
 */
const pointZoom = storelocator?.area_filter?.point_zoom || DEFAULT_POINT_ZOOM
const pointsMaxZoom = storelocator?.area_filter?.points_max_zoom || DEFAULT_POINTS_MAX_ZOOM

/**
 * 地域を選択して移動するモーダル
 */
let areaFilterModal: uikit.UIkitModalElement | null = null

/**
 * 集約した市町村が初期化されているか
 */
const initialized = ref(false)

/**
 * 選択中の市町村名
 */
const selectedCity = ref('')

/**
 * 集約した市町村
 * PointService.aggregates.cities.groups は市町村名のみで集約されている前提
 * @returns {Object} { 市町村名: { center: {}, bounds: [] }, ... }
 */
const groupedCities = computed<Record<string, AggregatedAreaGroup>>(() => PointService.aggregates?.cities?.groups || {})

/**
 * 市町村の選択肢（並び順は50音順）
 */
const cities = ref<string[]>([])

/**
 * true: 地図が読み込み完了
 */
const isMapReady = computed(() => store.state.mapReady === 0)

onMounted(() => {
  areaFilterModal = uikit.modal('#tochituka-area-filter-modal')
})

watch(() => isMapReady.value, (isMapReady) => {
  if (!initialized.value && isMapReady) {
    init()
  }
})

/**
 * 集約した市町村の読み込みを待って選択肢を作成
 */
const init = async () => {
  // 集約した地域を読み込むために待機
  await new Promise((resolve) => setTimeout(resolve, 100))

  // 読み込まれていない場合はさらに待機
  let attempts = 0
  while (!PointService.aggregates?.cities && attempts < 3) {
    await new Promise((resolve) => setTimeout(resolve, 1000))
    attempts++
  }

  if (PointService.aggregates?.cities) {
    initialized.value = true
    cities.value = Object.keys(groupedCities.value)
      .filter(city => city !== '')
      .sort((a, b) => a.localeCompare(b, 'ja'))
  } else {
    uikit.notification({
      message: messages.map_failed_to_load_areas,
      pos: 'top-center',
      timeout: 3000
    })
  }
}

/**
 * 移動ボタンクリック
 */
const onClickedMove = () => {
  const groupedArea = groupedCities.value[selectedCity.value]

  if (!groupedArea) {
    uikit.notification({
      message: messages.map_failed_to_load_areas,
      pos: 'top-center',
      timeout: 3000
    })
    return
  }

  fitBoundsFromGroupedArea(groupedArea)

  areaFilterModal?.hide()
  uikit.notification({
    message: messages.common_moved_to_area.replace('{area}', selectedCity.value),
    pos: 'top-center',
    timeout: 3000
  })

  // モーダルが非表示になるまで待機
  setTimeout(() => {
    selectedCity.value = ''
  }, 300)
}

/**
 * 集約した地域をもとにポイントの表示範囲を更新
 * @param groupedArea { center: {latitude, longitude}, bounds: [{latitude, longitude}, {latitude, longitude}] }
 */
const fitBoundsFromGroupedArea = (groupedArea: AggregatedAreaGroup) => {
  const [southWest, northEast] = groupedArea.bounds
  const isSinglePoint = southWest.latitude === northEast.latitude && southWest.longitude === northEast.longitude

  if (isSinglePoint) {
    // ポイントが1つの場合はそのポイントにズーム
    MapService.map.panTo(new google.maps.LatLng(groupedArea.center.latitude, groupedArea.center.longitude))
    MapService.map.setZoom(pointZoom)
  } else {
    // ポイントが複数の場合は全てのポイントを表示する
    const bounds = new google.maps.LatLngBounds()
    groupedArea.bounds.forEach(bound => bounds.extend(new google.maps.LatLng(bound.latitude, bound.longitude)))
    MapService.fitBounds(bounds, pointsMaxZoom)
  }
}
</script>

<style lang="scss" scoped>
.area-filter.move-only {
  button {
    background-color: #fff;
    border-radius: 15px;
    border: 1px solid #eee;
    height: 30px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

#tochituka-area-filter-modal {
  .uk-modal-dialog {
    width: 400px;
    padding: 0;
    .uk-modal-header {
      padding: 1rem 1.5rem 1rem 1rem;
      .uk-modal-title-small {
        color: #666;
        font-size: 1.125rem;
      }
    }
    .uk-modal-body {
      display: flex;
      flex-direction: column;
      align-items: center;
      row-gap: 1rem;

      select.uk-select {
        border: 1px solid #eee;
        border-radius: 2px;
        font-size: 0.875rem !important;
        text-align: center;
      }

      .move-to-area {
        height: 40px;
        padding: 0px 20px;
        transition: all 0.3s;
        border-radius: 20px;
        color: #fff;
        background-color: var(--theme-color);
        cursor: pointer;
        span {
          opacity: 0.8;
        }
        &:hover:not(:disabled) {
          span {
            opacity: 1;
          }
        }
        &:disabled {
          cursor: default;
          span {
            opacity: 0.4;
          }
        }
      }
    }
  }
}
</style>
